import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ProjectCreationModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    client: '',
    startDate: '',
    endDate: '',
    value: '',
    priority: 'medium',
    technicians: [],
    scope: '',
    objectives: '',
    deliverables: '',
    risks: '',
    budget: '',
    timeline: ''
  });

  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const clientOptions = [
    { value: 'abc-corp', label: 'ABC Corporation' },
    { value: 'xyz-ltd', label: 'XYZ Limitada' },
    { value: 'tech-solutions', label: 'Tech Solutions Brasil' },
    { value: 'global-services', label: 'Global Services Ltda' },
    { value: 'innovate-co', label: 'Innovate & Co' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Baixa' },
    { value: 'medium', label: 'Média' },
    { value: 'high', label: 'Alta' }
  ];

  const technicianOptions = [
    { value: 'joao-silva', label: 'João Silva - Especialista em Redes' },
    { value: 'maria-santos', label: 'Maria Santos - Analista de Sistemas' },
    { value: 'carlos-oliveira', label: 'Carlos Oliveira - Consultor Sênior' },
    { value: 'ana-costa', label: 'Ana Costa - Gerente de Projetos' },
    { value: 'pedro-ferreira', label: 'Pedro Ferreira - Técnico de Campo' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.name?.trim()) newErrors.name = 'Nome do projeto é obrigatório';
      if (!formData?.code?.trim()) newErrors.code = 'Código do projeto é obrigatório';
      if (!formData?.client) newErrors.client = 'Cliente é obrigatório';
      if (!formData?.startDate) newErrors.startDate = 'Data de início é obrigatória';
      if (!formData?.endDate) newErrors.endDate = 'Data de fim é obrigatória';
      if (formData?.startDate && formData?.endDate && new Date(formData.startDate) >= new Date(formData.endDate)) {
        newErrors.endDate = 'Data de fim deve ser posterior à data de início';
      }
    } else if (step === 2) {
      if (!formData?.scope?.trim()) newErrors.scope = 'Escopo é obrigatório';
      if (!formData?.objectives?.trim()) newErrors.objectives = 'Objetivos são obrigatórios';
      if (!formData?.value || parseFloat(formData?.value) <= 0) newErrors.value = 'Valor deve ser maior que zero';
    } else if (step === 3) {
      if (formData?.technicians?.length === 0) newErrors.technicians = 'Pelo menos um técnico deve ser selecionado';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = () => {
    if (validateStep(currentStep)) {
      // Generate project code if not provided
      const projectCode = formData?.code || `PROJ-${Date.now()}`;
      
      const projectData = {
        ...formData,
        code: projectCode,
        id: Date.now(),
        status: 'planning',
        progress: 0,
        serviceOrders: 0,
        createdAt: new Date()?.toISOString(),
        createdBy: 'Admin User'
      };

      onSave(projectData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      client: '',
      startDate: '',
      endDate: '',
      value: '',
      priority: 'medium',
      technicians: [],
      scope: '',
      objectives: '',
      deliverables: '',
      risks: '',
      budget: '',
      timeline: ''
    });
    setErrors({});
    setCurrentStep(1);
    onClose();
  };

  if (!isOpen) return null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nome do Projeto *"
                type="text"
                placeholder="Ex: Modernização de TI"
                value={formData?.name}
                onChange={(e) => handleInputChange('name', e?.target?.value)}
                error={errors?.name}
                required
              />
              <Input
                label="Código do Projeto"
                type="text"
                placeholder="Ex: PROJ-2024-001"
                value={formData?.code}
                onChange={(e) => handleInputChange('code', e?.target?.value)}
                error={errors?.code}
                description="Deixe em branco para gerar automaticamente"
              />
            </div>
            <Input
              label="Descrição"
              type="text"
              placeholder="Breve descrição do projeto"
              value={formData?.description}
              onChange={(e) => handleInputChange('description', e?.target?.value)}
            />
            <Select
              label="Cliente *"
              placeholder="Selecione o cliente"
              options={clientOptions}
              value={formData?.client}
              onChange={(value) => handleInputChange('client', value)}
              error={errors?.client}
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Data de Início *"
                type="date"
                value={formData?.startDate}
                onChange={(e) => handleInputChange('startDate', e?.target?.value)}
                error={errors?.startDate}
                required
              />
              <Input
                label="Data de Fim *"
                type="date"
                value={formData?.endDate}
                onChange={(e) => handleInputChange('endDate', e?.target?.value)}
                error={errors?.endDate}
                required
              />
              <Select
                label="Prioridade"
                options={priorityOptions}
                value={formData?.priority}
                onChange={(value) => handleInputChange('priority', value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Valor do Projeto *"
                type="number"
                placeholder="0,00"
                value={formData?.value}
                onChange={(e) => handleInputChange('value', e?.target?.value)}
                error={errors?.value}
                description="Valor em R$"
                required
              />
              <Input
                label="Orçamento Aprovado"
                type="number"
                placeholder="0,00"
                value={formData?.budget}
                onChange={(e) => handleInputChange('budget', e?.target?.value)}
                description="Orçamento total aprovado"
              />
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Escopo do Projeto *
                </label>
                <textarea
                  className="w-full min-h-[100px] px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                  placeholder="Descreva o escopo detalhado do projeto..."
                  value={formData?.scope}
                  onChange={(e) => handleInputChange('scope', e?.target?.value)}
                />
                {errors?.scope && (
                  <p className="text-sm text-error mt-1">{errors?.scope}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Objetivos *
                </label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                  placeholder="Liste os principais objetivos do projeto..."
                  value={formData?.objectives}
                  onChange={(e) => handleInputChange('objectives', e?.target?.value)}
                />
                {errors?.objectives && (
                  <p className="text-sm text-error mt-1">{errors?.objectives}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Entregas Esperadas
                </label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                  placeholder="Descreva as principais entregas..."
                  value={formData?.deliverables}
                  onChange={(e) => handleInputChange('deliverables', e?.target?.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Riscos Identificados
                </label>
                <textarea
                  className="w-full min-h-[80px] px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                  placeholder="Liste os principais riscos e mitigações..."
                  value={formData?.risks}
                  onChange={(e) => handleInputChange('risks', e?.target?.value)}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Técnicos Responsáveis *
              </label>
              <div className="space-y-2 max-h-60 overflow-y-auto border border-border rounded-md p-3">
                {technicianOptions?.map((option) => (
                  <Checkbox
                    key={option?.value}
                    label={option?.label}
                    checked={formData?.technicians?.includes(option?.value)}
                    onChange={(e) => {
                      const isChecked = e?.target?.checked;
                      const newTechnicians = isChecked
                        ? [...formData?.technicians, option?.value]
                        : formData?.technicians?.filter(id => id !== option?.value);
                      handleInputChange('technicians', newTechnicians);
                    }}
                  />
                ))}
              </div>
              {errors?.technicians && (
                <p className="text-sm text-error mt-1">{errors?.technicians}</p>
              )}
              <p className="text-sm text-muted-foreground mt-2">
                {formData?.technicians?.length} técnico(s) selecionado(s)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Cronograma Detalhado
              </label>
              <textarea
                className="w-full min-h-[120px] px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                placeholder={`Descreva o cronograma do projeto:\n\nFase 1: Planejamento (Semana 1-2)\nFase 2: Execução (Semana 3-8)\nFase 3: Testes (Semana 9-10)\nFase 4: Entrega (Semana 11-12)`}
                value={formData?.timeline}
                onChange={(e) => handleInputChange('timeline', e?.target?.value)}
              />
            </div>
            {/* Project Summary */}
            <div className="bg-muted/50 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-foreground mb-3">Resumo do Projeto</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Nome:</span>
                  <span className="ml-2 font-medium">{formData?.name || 'Não informado'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Cliente:</span>
                  <span className="ml-2 font-medium">
                    {clientOptions?.find(c => c?.value === formData?.client)?.label || 'Não selecionado'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Período:</span>
                  <span className="ml-2 font-medium">
                    {formData?.startDate && formData?.endDate 
                      ? `${new Date(formData.startDate)?.toLocaleDateString('pt-BR')} - ${new Date(formData.endDate)?.toLocaleDateString('pt-BR')}`
                      : 'Não definido'
                    }
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Valor:</span>
                  <span className="ml-2 font-medium">
                    {formData?.value 
                      ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })?.format(formData?.value)
                      : 'Não informado'
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg shadow-modal w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              Criar Novo Projeto
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Etapa {currentStep} de {totalSteps}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center space-x-4">
            {[1, 2, 3]?.map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step < currentStep 
                    ? 'bg-success text-success-foreground' 
                    : step === currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {step < currentStep ? (
                    <Icon name="Check" size={16} />
                  ) : (
                    step
                  )}
                </div>
                <div className="ml-2 text-sm">
                  <p className={`font-medium ${step <= currentStep ? 'text-foreground' : 'text-muted-foreground'}`}>
                    {step === 1 ? 'Informações Básicas' : step === 2 ? 'Escopo e Objetivos' : 'Equipe e Cronograma'}
                  </p>
                </div>
                {step < totalSteps && (
                  <div className={`w-12 h-0.5 mx-4 ${step < currentStep ? 'bg-success' : 'bg-muted'}`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            * Campos obrigatórios
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              Cancelar
            </Button>
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Anterior
              </Button>
            )}
            {currentStep < totalSteps ? (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
              >
                Próximo
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                iconName="Save"
                iconPosition="left"
              >
                Criar Projeto
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreationModal;